/* eslint-disable jsx-a11y/anchor-is-valid */
import { Dispatch, SetStateAction, useMemo, type JSX } from 'react';
import { useIntl } from 'react-intl';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import type Trail from '@alltrails/types/Trail';
import InfoDividerDot from '@alltrails/shared/components/cards/InfoDividerDot';
import { millisecondsInDay } from '@alltrails/data-formatting/dateConstants';
import { photoTimeStamp } from '@alltrails/data-formatting/dateTimeFormatters';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import { LinkInfo, DropdownMenuOption, LightboxDropdownMenuState, LightboxUser } from '../../types';
import useIsMobileSizedScreen from '../../hooks/useIsMobileSizedScreen';
import Close from '../../icons/Close';
import Grid from '../../icons/Grid';
import Overflow from '../../icons/Overflow';
import Link from '../Link/Link';
import Avatar from '../Avatar/Avatar';
import Tooltip from '../Tooltip/Tooltip';
import IconButton from '../IconButton/IconButton';
import DropdownMenu from '../DropdownMenu/DropdownMenu';
import styles from './styles/styles.module.scss';

export function isHumanizedTimeStamp(timestamp: string) {
  const uploadDate: Date = new Date(timestamp);
  const today: Date = new Date(Date.now());
  return !!((+today - +uploadDate) / millisecondsInDay < 7);
}

export type LightboxHeaderProps = {
  user?: LightboxUser;
  timestamp?: string;
  languageRegionCode: LanguageRegionCode;
  linkedTrail?: Pick<Trail, 'name' | 'slug'>;
  creditTitle?: { title: string; user_name?: string; user_profile_url?: string; attribution?: string };
  onClose: () => void;
  onDropdownMenuClick?: () => void;
  onUserClick?: (user: LightboxUser) => void;
  dropdownMenu?: LightboxDropdownMenuState;
  setDropdownMenuOptions?: Dispatch<SetStateAction<LightboxDropdownMenuState>>;
  dropdownOptions?: DropdownMenuOption[];
  shouldHideActions?: boolean;
  photosPageLinkInfo?: LinkInfo;
  onPhotosPageLinkClick?: () => void;
  showTimestampOnly?: boolean;
};

// https://www.figma.com/file/KYgIXAiEuiuBQoIQ5Tm3qf/Lightbox?type=design&node-id=12-9477&mode=design&t=7z0lakmFOLdgvB7t-0
const LightboxHeader = ({
  user,
  timestamp,
  languageRegionCode,
  linkedTrail,
  creditTitle,
  onClose,
  onDropdownMenuClick,
  onUserClick,
  dropdownMenu,
  setDropdownMenuOptions,
  dropdownOptions,
  shouldHideActions,
  photosPageLinkInfo,
  onPhotosPageLinkClick,
  showTimestampOnly
}: LightboxHeaderProps): JSX.Element => {
  const intl = useIntl();
  const isMobile = useIsMobileSizedScreen();
  const date = timestamp && photoTimeStamp(timestamp, languageRegionCode);
  const showTooltip = timestamp && isHumanizedTimeStamp(timestamp);

  const photoUploadDate = useMemo(() => <div className={styles.timestamp}>{date}</div>, [date]);

  const tooltip = useMemo(() => {
    const dateFormatter = new Intl.DateTimeFormat(languageRegionCode, { year: 'numeric', month: 'short', day: 'numeric' });
    return (
      <Tooltip
        anchorElement={photoUploadDate}
        testId="date-tooltip"
        text={timestamp ? dateFormatter.format(new Date(timestamp)) : ''}
        tooltipPlacement="bottom"
        hidePointer
      />
    );
  }, [photoUploadDate, timestamp, languageRegionCode]);

  const renderUploadDate = () => (showTooltip ? tooltip : photoUploadDate);

  return (
    <div className={styles.header}>
      {(user || creditTitle) && !shouldHideActions ? (
        <div className={styles.userInfo}>
          {user && creditTitle?.user_name && (
            <Avatar
              eager
              userName={user.userName}
              userId={user.id}
              testId={`lightbox-user-avatar-${user.userName.replace(' ', '-')}`}
              linkInfo={{ href: wrapUrlSafe(user.profileUrl, languageRegionCode) }}
              onClick={() => onUserClick?.(user)}
            />
          )}

          <div className={styles.userContainer}>
            <div className={styles.userDetails}>
              {(user?.userName || creditTitle?.user_name) && !creditTitle?.attribution && (
                <div className={styles.userName}>{user?.userName || creditTitle?.user_name}</div>
              )}
            </div>

            <div className={styles.secondaryText}>
              {!isMobile ? renderUploadDate() : null}
              {linkedTrail && (
                <span className={styles.trailName} data-testid="trail-name">
                  {!isMobile ? <InfoDividerDot className={styles.dividerDot} /> : null}
                  <Link
                    className={styles.trailName}
                    variant="inverse"
                    noUnderline
                    testId="linked-trail"
                    href={`/${linkedTrail.slug}`}
                    target="_blank"
                  >
                    {linkedTrail.name}
                  </Link>
                </span>
              )}
            </div>
          </div>
        </div>
      ) : null}
      {timestamp && showTimestampOnly && <div className={styles.userContainer}>{renderUploadDate()}</div>}
      <div className={styles.iconsContainer}>
        {photosPageLinkInfo && (
          <IconButton
            icon={{ Component: Grid }}
            onClick={onPhotosPageLinkClick}
            title={intl.formatMessage({ defaultMessage: 'All photos' })}
            testId="Lightbox_allphotos"
            className={styles.iconButton}
            variant="transparent"
            size={isMobile ? 'sm' : 'md'}
            linkInfo={photosPageLinkInfo}
          />
        )}
        {dropdownMenu &&
          dropdownOptions &&
          dropdownOptions.length > 0 &&
          setDropdownMenuOptions &&
          !isMobile &&
          (!shouldHideActions || showTimestampOnly) && (
            <div className={styles.overflow}>
              <DropdownMenu
                anchor={
                  <IconButton
                    icon={{ Component: Overflow }}
                    onClick={onDropdownMenuClick}
                    title={intl.formatMessage({ defaultMessage: 'More actions' })}
                    testId="LightboxMenu_Button"
                    className={styles.iconButton}
                    variant="transparent"
                    size="md"
                  />
                }
                isOpen={dropdownMenu?.isOpen}
                onCloseRequest={() => setDropdownMenuOptions({ isOpen: false, options: [] })}
                options={dropdownMenu?.options}
                placement="bottom-end"
              />
            </div>
          )}
        <IconButton
          icon={{ Component: Close }}
          onClick={onClose}
          title={intl.formatMessage({ defaultMessage: 'Close' })}
          testId="LightboxClose_Button"
          className={styles.iconButton}
          variant="transparent"
          size={isMobile ? 'sm' : 'md'}
        />
      </div>
    </div>
  );
};

export default LightboxHeader;
